import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { LinkButton, Icon } from '@evertrue/et-components';
import RecipientInput from './recipient-input';
import useOutsideClick from 'hooks/use-outside-click';

export const MultiRecipientsInput = ({ values, setFieldValue }) => {
  // State to track active field and focus
  const [activeField, setActiveField] = useState(null);
  const [focused, setFocused] = useState(false);

  // Refs for the recipient inputs
  const containerRef = useRef(null);
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const bccInputRef = useRef(null);

  // Use the existing outside click hook
  useOutsideClick(containerRef, () => {
    // Don't collapse if clicking on a cancel icon (handled in the icon's onClick)
    if (!document.activeElement?.classList?.contains('email-recipient-label-cross-icon')) {
      setFocused(false);
    }
  });

  // Handle container click to expand the fields
  const handleContainerClick = () => {
    if (!focused) {
      setFocused(true);

      // Check if there are CC or BCC recipients to determine which fields to show
      const hasCc = Array.isArray(values.cc) && values.cc.length > 0;
      const hasBcc = Array.isArray(values.bcc) && values.bcc.length > 0;

      // Set the active field to "to" by default
      setActiveField('to');

      // If there are CC recipients, show the CC field
      if (hasCc) {
        setActiveField('cc');
      }

      // If there are BCC recipients, show the BCC field
      if (hasBcc) {
        setActiveField('bcc');
      }
    }
  };

  // Handle field focus
  const handleFieldFocus = (fieldName) => {
    setFocused(true);
    setActiveField(fieldName);
  };

  // Activate CC field
  const handleShowCc = () => {
    setFocused(true);
    setActiveField('cc');
  };

  // Activate BCC field
  const handleShowBcc = () => {
    setFocused(true);
    setActiveField('bcc');
  };

  // Format recipients for collapsed view
  const formatRecipients = () => {
    const allRecipients = [];
    const toRecipients = Array.isArray(values.to) ? values.to : [];
    const ccRecipients = Array.isArray(values.cc) ? values.cc : [];
    const bccRecipients = Array.isArray(values.bcc) ? values.bcc : [];

    // Add labels to recipients
    toRecipients.forEach((email) => allRecipients.push({ email, type: 'to' }));
    ccRecipients.forEach((email) => allRecipients.push({ email, type: 'cc' }));
    bccRecipients.forEach((email) => allRecipients.push({ email, type: 'bcc' }));

    return allRecipients;
  };

  // Remove a recipient from any field
  const removeRecipient = (email, type) => {
    const fieldName = type || 'to';
    const currentList = Array.isArray(values[fieldName]) ? [...values[fieldName]] : [];
    const updatedList = currentList.filter((item) => item !== email);
    setFieldValue(fieldName, updatedList);
  };

  // Render the collapsed view (when not focused)
  const renderCollapsedView = () => {
    const allRecipients = formatRecipients();

    // If no recipients, show a placeholder with "To:"
    if (allRecipients.length === 0) {
      return (
        <div className="email-form-header-field" onClick={handleContainerClick}>
          <label className="email-form--label">
            <strong className="email-form-header-label">To</strong>
            <span className="email-recipients-placeholder">Click to add recipients</span>
          </label>
        </div>
      );
    }

    return (
      <div className="email-form-header-field" onClick={handleContainerClick}>
        <label className="email-form--label email-recipients-with-content">
          {/* No label when there are recipients */}
          <ul className="email-recipients-field">
            {allRecipients.slice(0, 5).map((item, index) => (
              <li className="email-recipients-field-tag" key={`${item.email}-${index}`}>
                {item.type !== 'to' && <span className="recipient-type">{item.type}: </span>}
                {item.email}
                <Icon
                  icon="cancel"
                  size={1}
                  after={true}
                  className="email-recipient-label-cross-icon"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent opening the expanded view
                    removeRecipient(item.email, item.type);
                  }}
                  style={{ margin: '5px', cursor: 'pointer' }}
                />
              </li>
            ))}
            {allRecipients.length > 5 && (
              <li className="email-recipients-field-tag">{allRecipients.length - 5} more...</li>
            )}
          </ul>
        </label>
      </div>
    );
  };

  // Render the expanded view (when focused)
  const renderExpandedView = () => {
    const hasCc = Array.isArray(values.cc) && values.cc.length > 0;
    const hasBcc = Array.isArray(values.bcc) && values.bcc.length > 0;

    return (
      <>
        <div className="email-recipients-row">
          <RecipientInput
            recipients={values.to}
            setFieldValue={setFieldValue}
            fieldName="to"
            label="To"
            inputRef={toInputRef}
            onFocus={() => handleFieldFocus('to')}
            isActive={activeField === 'to'}
          />
          <div className="email-form-header-cc">
            <LinkButton title="Add Cc" onClick={handleShowCc}>
              Cc
            </LinkButton>
            <LinkButton title="Add Bcc" onClick={handleShowBcc}>
              Bcc
            </LinkButton>
          </div>
        </div>

        {/* Always show CC row if it has recipients or is active */}
        {(activeField === 'cc' || hasCc) && (
          <div className="email-recipients-row cc-row">
            <RecipientInput
              recipients={values.cc}
              setFieldValue={setFieldValue}
              fieldName="cc"
              label="Cc"
              inputRef={ccInputRef}
              onFocus={() => handleFieldFocus('cc')}
              isActive={activeField === 'cc'}
            />
          </div>
        )}

        {/* Always show BCC row if it has recipients or is active */}
        {(activeField === 'bcc' || hasBcc) && (
          <div className="email-recipients-row bcc-row">
            <RecipientInput
              recipients={values.bcc}
              setFieldValue={setFieldValue}
              fieldName="bcc"
              label="Bcc"
              inputRef={bccInputRef}
              onFocus={() => handleFieldFocus('bcc')}
              isActive={activeField === 'bcc'}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="email-form--recipients-container" ref={containerRef}>
      <div className="email-recipients-main-container">{focused ? renderExpandedView() : renderCollapsedView()}</div>
    </div>
  );
};

MultiRecipientsInput.propTypes = {
  values: PropTypes.shape({
    to: PropTypes.array,
    cc: PropTypes.array,
    bcc: PropTypes.array,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
};
